import { createPermissionPayload } from '@adornis/abac/policies/SimpleRolePolicy.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { context } from '@adornis/baseql/server/context.js';
import type { SNFieldValue, SNSmartTeamTicket } from '@nsc/servicenow/types.js';
import { expectedError } from '@nsc/smartteams-shared/api/expected-error.js';
import { SmartTeamsAppUser } from '../db/SmartTeamsAppUser.js';
import { accessController } from '../features/permissions/init-permissions.js';
import { SmartTeamsAppPermission, SmartTeamsAppUserGroup } from '../features/permissions/roles-and-permissions.js';

/**
 * Resolve a ticket's assignee field across API response shapes. The live SmartTeams API moved
 * `assigned_to` under a nested `assignment` group (verified 2026-06-25 against nsctest, on both
 * `ticket_list` and `ticket_details`: `…assignment.assigned_to`); older revisions returned it flat
 * on the ticket. Read the nested path first, fall back to the flat one. Without this every
 * `assigned_to` read resolves to `undefined`, which silently breaks FE scoping and the assigned-FE
 * action guards (they compare against `undefined` and reject/drop everything).
 */
export const snAssignedTo = (sn: SNSmartTeamTicket): SNFieldValue | undefined =>
  sn.assignment?.assigned_to ?? sn.assigned_to;

/**
 * Resolve the SmartTeamsAppUser for the current request, throwing if there is no
 * authenticated user or no app user record.
 */
export async function getCurrentAppUser(): Promise<SmartTeamsAppUser> {
  const userId = (context as any).userID as string | undefined;
  if (!userId) throw expectedError('Not authenticated');
  const col = await getRawCollection<SmartTeamsAppUser>(SmartTeamsAppUser._collectionName);
  const user = await col.findOne({ userId });
  if (!user) throw expectedError('User profile not found');
  return new SmartTeamsAppUser(user);
}

/**
 * Server-side gate for any status-changing mutation:
 *   1. Caller must hold `TicketUpdate` (so admins/customers/partner managers fail before we even fetch).
 *   2. Caller must be a Field Engineer with `serviceNowFEId` set.
 *   3. The FE's `serviceNowFEId` must equal the ticket's `assigned_to` sys_id.
 *
 * Returns the resolved app user so the caller can avoid an extra fetch.
 */
export async function assertAssignedFE(ticketAssignedToSysId: string | null | undefined): Promise<SmartTeamsAppUser> {
  const allowed = await accessController.can(createPermissionPayload(SmartTeamsAppPermission.TicketUpdate));
  if (!allowed) throw expectedError('Insufficient permissions');

  const appUser = await getCurrentAppUser();
  if (appUser.userGroup !== SmartTeamsAppUserGroup.FieldEngineer) {
    throw expectedError('Only the assigned field engineer can update this ticket');
  }
  if (!appUser.serviceNowFEId) {
    throw expectedError('Field engineer is not linked to a ServiceNow account');
  }
  if (!ticketAssignedToSysId || appUser.serviceNowFEId !== ticketAssignedToSysId) {
    throw expectedError('Only the assigned field engineer can update this ticket');
  }

  return appUser;
}

/**
 * Decide whether the current Customer-userGroup app user owns the given ticket.
 *
 * A user can be linked to multiple SmartTeamsCustomer entities (each with its own SN
 * client sys_id); membership matches if ANY of those sys_ids matches.
 *
 * Preferred path: ServiceNow `ticket_details` returns the `client` field — we compare
 * sys_ids directly. Until that ships, we fall back to a list-membership check against
 * `ticket_list` filtered per-customer, so a customer can't open a ticket detail by
 * guessing its number.
 */
export async function isCustomerOfTicket(params: {
  appUser: SmartTeamsAppUser;
  ticketNumber: string | null | undefined;
  ticketClientSysId: string | null | undefined;
}): Promise<boolean> {
  const { appUser, ticketNumber, ticketClientSysId } = params;
  if (appUser.userGroup !== SmartTeamsAppUserGroup.Customer) return false;

  const { resolveCustomerSysIds } = await import('./_customer-membership.js');
  const sysIds = await resolveCustomerSysIds(appUser);
  if (sysIds.length === 0) return false;

  if (ticketClientSysId) return sysIds.includes(ticketClientSysId);
  if (!ticketNumber) return false;

  const { getTicketList } = await import('@nsc/servicenow/smartteam-api.js');
  const lists = await Promise.all(sysIds.map(clientId => getTicketList({ clientId, limit: 200 })));
  return lists.some(list => list.some(t => t.number?.display_value === ticketNumber));
}
